import React, { useContext, useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import { AuthContext } from "./context/AuthContext";
import Topbar from './components/Topbar';
import { ChevronRight, Database, BarChart, Search, Filter, Briefcase, Users, ShoppingCart, Activity, Heart, DollarSign } from 'lucide-react';

const CasestudyCards = () => {
    const { userId } = useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDifficulty, setSelectedDifficulty] = useState("all");
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Determine grid columns based on screen size
    const getGridColumns = () => {
        if (screenSize.width >= 1920) return 'grid-cols-4'; // Full HD and larger
        if (screenSize.width >= 1280) return 'grid-cols-3'; // HD
        if (screenSize.width >= 768) return 'grid-cols-2';  // Tablet
        return 'grid-cols-1';                               // Mobile
    };

    // Determine card and text sizes based on screen size
    const getResponsiveClasses = () => {
        if (screenSize.width >= 1920) {
            return {
                card: 'p-8',
                title: 'text-2xl',
                description: 'text-base',
                icon: '40',
                heroTitle: 'text-5xl',
                heroText: 'text-xl'
            };
        }
        if (screenSize.width >= 1280) {
            return {
                card: 'p-6',
                title: 'text-xl',
                description: 'text-sm',
                icon: '32',
                heroTitle: 'text-4xl',
                heroText: 'text-lg'
            };
        }
        return {
            card: 'p-4',
            title: 'text-lg',
            description: 'text-sm',
            icon: '24',
            heroTitle: 'text-3xl',
            heroText: 'text-base'
        };
    };

    const responsiveClasses = getResponsiveClasses();

    const casestudies = [
        {
            id: 1,
            title: 'E-commerce Sales Analysis',
            description: 'Deep dive into sales performance, customer behavior, and revenue patterns across multiple product categories and regions.',
            icon: <ShoppingCart className="text-blue-500" size={32} />,
            difficulty: 'Intermediate',
            tags: ['Sales Analytics', 'Customer Segmentation', 'Revenue Analysis'],
            completionRate: 75,
            estimatedTime: '2-3 hours'
        },
        {
            id: 2,
            title: 'Hospital Patient Management',
            description: 'Complex healthcare database analysis focusing on patient care metrics, resource utilization, and treatment outcomes.',
            icon: <Heart className="text-rose-500" size={32} />,
            difficulty: 'Advanced',
            tags: ['Healthcare', 'Patient Analytics', 'Resource Management'],
            completionRate: 60,
            estimatedTime: '3-4 hours'
        },
        {
            id: 3,
            title: 'Social Media Engagement',
            description: 'Comprehensive analysis of user engagement patterns, content performance metrics, and audience growth strategies.',
            icon: <Users className="text-purple-500" size={32} />,
            difficulty: 'Intermediate',
            tags: ['Social Analytics', 'User Engagement', 'Content Metrics'],
            completionRate: 85,
            estimatedTime: '2-3 hours'
        },
        {
            id: 4,
            title: 'Financial Transactions',
            description: 'In-depth financial data analysis covering transaction patterns, risk assessment, and fraud detection scenarios.',
            icon: <DollarSign className="text-emerald-500" size={32} />,
            difficulty: 'Advanced',
            tags: ['Finance', 'Risk Analysis', 'Fraud Detection'],
            completionRate: 45,
            estimatedTime: '4-5 hours'
        },
        {
            id: 5,
            title: 'Business Intelligence',
            description: 'Strategic business data analysis focusing on market trends, competitive analysis, and growth opportunities.',
            icon: <Briefcase className="text-indigo-500" size={32} />,
            difficulty: 'Advanced',
            tags: ['Market Analysis', 'Business Strategy', 'Competitive Intel'],
            completionRate: 65,
            estimatedTime: '3-4 hours'
        },
        {
            id: 6,
            title: 'Performance Metrics',
            description: 'Comprehensive system performance analysis covering efficiency metrics, optimization opportunities, and bottleneck identification.',
            icon: <Activity className="text-orange-500" size={32} />,
            difficulty: 'Intermediate',
            tags: ['Performance', 'Optimization', 'Metrics'],
            completionRate: 70,
            estimatedTime: '2-3 hours'
        }
    ];

    const filteredCaseStudies = casestudies.filter(study => {
        const matchesSearch = study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            study.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDifficulty = selectedDifficulty === "all" || study.difficulty.toLowerCase() === selectedDifficulty.toLowerCase();
        return matchesSearch && matchesDifficulty;
    });

    const CaseStudyCard = ({ study }) => {
        const getDifficultyColor = (difficulty) => {
            switch (difficulty) {
                case 'Beginner': return 'bg-green-100 text-green-800 border-green-200';
                case 'Intermediate': return 'bg-blue-100 text-blue-800 border-blue-200';
                case 'Advanced': return 'bg-red-100 text-red-800 border-red-200';
                default: return 'bg-gray-100 text-gray-800 border-gray-200';
            }
        };

        return (
            <Link
                to={`/case-study/${study.id}`}
                className="group transform transition-all duration-300 hover:scale-[1.02]"
            >
                <div className={`bg-white rounded-2xl ${responsiveClasses.card} border border-gray-100 hover:border-blue-100 hover:shadow-xl transition-all duration-300 h-full flex flex-col relative overflow-hidden`}>
                    {/* Progress indicator */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gray-100">
                        <div
                            className="h-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${study.completionRate}%` }}
                        />
                    </div>

                    <div className="flex justify-between items-start mb-4 pt-2">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100">
                            {React.cloneElement(study.icon, { size: parseInt(responsiveClasses.icon) })}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(study.difficulty)}`}>
                                {study.difficulty}
                            </span>
                            <span className="text-xs text-gray-500">
                                {study.estimatedTime}
                            </span>
                        </div>
                    </div>

                    <h3 className={`${responsiveClasses.title} font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors`}>
                        {study.title}
                    </h3>

                    <p className={`${responsiveClasses.description} text-gray-600 mb-4 flex-grow leading-relaxed`}>
                        {study.description}
                    </p>

                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                            {study.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-gray-50 text-gray-600 text-xs rounded-full border border-gray-100"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                            <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:text-blue-700">
                                Start Case Study
                                <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                            <span className="text-sm text-gray-500">{study.completionRate}% Complete</span>
                        </div>
                    </div>
                </div>
            </Link>
        );
    };

    return (
        <div className="relative flex h-screen overflow-hidden justify-center bg-gray-50">
            <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto h-screen overflow-hidden">
                <Topbar />

                <div className="container mx-auto px-4 py-8 overflow-y-auto">
                    {/* Hero Section */}
                    <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 lg:p-8 mb-8 overflow-hidden">
                        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
                        <div className="relative z-10 max-w-3xl">
                            <h1 className={`${responsiveClasses.heroTitle} font-bold text-white mb-4`}>
                                Master SQL through Real-World Case Studies
                            </h1>
                            <p className={`${responsiveClasses.heroText} text-blue-100 mb-6`}>
                                Dive into comprehensive business scenarios and strengthen your SQL expertise with hands-on practice using real-world data challenges.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center bg-white/20 rounded-full px-4 py-2 text-white">
                                    <Database size={screenSize.width >= 1280 ? 20 : 16} className="mr-2" />
                                    <span className="text-sm">6 Case Studies</span>
                                </div>
                                <div className="flex items-center bg-white/20 rounded-full px-4 py-2 text-white">
                                    <BarChart size={screenSize.width >= 1280 ? 20 : 16} className="mr-2" />
                                    <span className="text-sm">40+ SQL Challenges</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="bg-white rounded-xl shadow-sm p-4 lg:p-6 mb-8 border border-gray-100">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search case studies..."
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <select
                                        className="appearance-none pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[150px]"
                                        value={selectedDifficulty}
                                        onChange={(e) => setSelectedDifficulty(e.target.value)}
                                    >
                                        <option value="all">All Levels</option>
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                    </select>
                                    <Filter className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                                    <ChevronRight className="absolute right-3 top-2.5 text-gray-400 w-5 h-5 rotate-90" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Case Studies Grid */}
                    <div className={`grid ${getGridColumns()} gap-4 lg:gap-6`}>
                        {filteredCaseStudies.map(study => (
                            <CaseStudyCard key={study.id} study={study} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CasestudyCards;